import {graphql, useFragment} from 'react-relay'
import {CreateIssueDialogEntry, type CreateIssueDialogEntryProps} from '@github-ui/issue-create/CreateIssueDialogEntry'
import type {SubIssuesCreateDialog$key} from './__generated__/SubIssuesCreateDialog.graphql'
import {noop} from '@github-ui/noop'
import {useAlert} from '../utils/use-alert'
import {useCallback, useMemo} from 'react'
import {RelationshipsAlertDialog} from '@github-ui/issue-viewer/RelationshipsAlertDialog'

export function SubIssuesCreateDialog({
  issue,
  open,
  setOpen,
  onCreateSuccess,
}: {
  issue: SubIssuesCreateDialog$key
  open: boolean
  setOpen: (open: boolean) => void
  onCreateSuccess: CreateIssueDialogEntryProps['onCreateSuccess']
}) {
  const {alert, resetAlert, showServerAlert} = useAlert()
  const data = useFragment(
    // !! NOTE: This fragment is preloaded on the server via the primary IssueViewer query, so the data
    // here is loaded whether the flag is enabled or not. Please be careful and don't include fields which
    // are not already loaded by other fragments.
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SubIssuesCreateDialog on Issue {
        id
        repository {
          id
          name
          owner {
            login
          }
        }
        projectItems(first: 10) @connection(key: "ProjectSection_projectItems") {
          edges {
            node {
              project {
                # eslint-disable-next-line relay/unused-fields
                id
                # eslint-disable-next-line relay/unused-fields
                title
                # eslint-disable-next-line relay/unused-fields
                closed
                # eslint-disable-next-line relay/unused-fields
                number
                # eslint-disable-next-line relay/unused-fields
                url
                # eslint-disable-next-line relay/unused-fields
                viewerCanUpdate
                # eslint-disable-next-line relay/unused-fields
                hasReachedItemsLimit
                # eslint-disable-next-line relay/unused-fields
                __typename
              }
            }
          }
        }
        milestone {
          id
          # eslint-disable-next-line relay/unused-fields
          title
          # eslint-disable-next-line relay/unused-fields
          closed
          # eslint-disable-next-line relay/unused-fields
          dueOn
          # eslint-disable-next-line relay/unused-fields
          progressPercentage
          # eslint-disable-next-line relay/unused-fields
          url
          # eslint-disable-next-line relay/unused-fields
          closedAt
        }
      }
    `,
    issue,
  )

  const onCreateError = useCallback((error: Error) => showServerAlert(error), [showServerAlert])

  // Extract projects from the parent issue to inherit them in the sub-issue
  const preselectedProjects = useMemo(() => {
    const projectItems = data.projectItems?.edges ?? []
    return projectItems
      .flatMap(edge => (edge?.node ? [edge.node] : []))
      .filter(projectItem => projectItem.project.closed !== true)
      .map(projectItem => projectItem.project)
  }, [data.projectItems?.edges])

  // Extract milestone from the parent issue to inherit it in the sub-issue
  // Only inherit if the milestone exists (milestones are repository-scoped, so this is always valid for sub-issues)
  const preselectedMilestone = data.milestone ? data.milestone : undefined

  const issueCreateProps: CreateIssueDialogEntryProps = {
    navigate: noop,
    onCreateSuccess,
    onCreateError,
    isCreateDialogOpen: open,
    setIsCreateDialogOpen: setOpen,
    optionConfig: {
      scopedOrganization: data.repository.owner.login,
      issueCreateArguments: {
        repository: {
          owner: data.repository.owner.login,
          name: data.repository.name,
        },
        parentIssue: {
          id: data.id,
        },
        initialValues: {
          projects: preselectedProjects,
          milestone: preselectedMilestone,
        },
      },
    },
  }

  return (
    <>
      {alert && (
        <RelationshipsAlertDialog title={alert.title} onClose={resetAlert}>
          {alert.body}
        </RelationshipsAlertDialog>
      )}
      <CreateIssueDialogEntry {...issueCreateProps} />
    </>
  )
}

try{ SubIssuesCreateDialog.displayName ||= 'SubIssuesCreateDialog' } catch {}